import React from 'react';
import { formatSoldTicketsWithTotal } from '../../api/gameApi';

function GameInfoPanel({
  game,
  roundId,
  soldTickets,
  roundLoading,
  roundError,
  vaultCreditUsdLabel = null,
  vaultCreditLoading = false,
  walletConnected = false,
}) {
  if (!game) return null;
  let soldTicketsLabel = '—';
  if (roundLoading) soldTicketsLabel = '…';
  else if (soldTickets !== null && soldTickets !== undefined) {
    soldTicketsLabel = formatSoldTicketsWithTotal(soldTickets, game.id);
  }

  const roundLabel = roundLoading
    ? '…'
    : roundId !== null && roundId !== undefined
      ? `#${roundId.toString()}`
      : '—';

  const creditAmount =
    !walletConnected ? (
      '—'
    ) : vaultCreditLoading ? (
      <span className="text-lg text-gray-400">…</span>
    ) : (
      vaultCreditUsdLabel ?? '—'
    );

  return (
    <div className="grid md:grid-cols-2 gap-4 mb-6">
      {/* Left — tagline + vault credit */}
      <div className="glass-card-gold-border relative overflow-hidden rounded-xl p-0">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_100%_70%_at_50%_-10%,rgba(238,212,91,0.12),transparent_55%)]"
          aria-hidden
        />

        <div
          className="relative border-b border-hash-gold/20 bg-gradient-to-r from-hash-gold/10 via-dark-surface/80 to-medora-blue/10 px-5 py-4"
          title={walletConnected ? 'Vault credit available for ticket purchases' : 'Connect wallet to view credit'}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                Your credit
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {walletConnected ? 'Available for tickets' : 'Connect wallet'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold leading-none tabular-nums text-hash-gold text-shadow-gold sm:text-3xl">
                {creditAmount}
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">USD</p>
            </div>
          </div>
        </div>

        <div className="relative px-5 pb-5 pt-4">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-hash-gold/25 bg-dark/50 shadow-[inset_0_1px_0_rgba(238,212,91,0.08)] md:h-16 md:w-16">
              <img
                src={game.icon}
                alt=""
                className="h-10 w-10 object-contain drop-shadow-[0_0_10px_rgba(238,212,91,0.2)] md:h-12 md:w-12"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <img
                  src="/images/text-the-hash.png"
                  alt="The Hash"
                  className="h-5 w-auto object-contain md:h-6"
                />
                <img
                  src={game.textImage}
                  alt={game.shortName}
                  className="h-5 w-auto object-contain md:h-6"
                />
              </div>
              {game.tier ? (
                <span className="mt-2 inline-flex rounded-md border border-hash-gold/30 bg-hash-gold/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-hash-gold">
                  {game.tier}
                </span>
              ) : null}
            </div>
          </div>
          <p className="text-sm leading-relaxed text-gray-300">{game.tagline}</p>
          <p className="mt-2 text-xs font-medium tracking-wide text-hash-gold/90">{game.subTagline}</p>
        </div>
      </div>

      {/* Right — round stats */}
      <div className="glass-card-blue-border p-5 rounded-xl">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-gray-500 text-xs mb-1">Round</div>
            <div className="text-hash-gold font-bold tabular-nums">{roundLabel}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">Sold tickets</div>
            <div className="text-white font-bold tabular-nums">{soldTicketsLabel}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">Entry Fee</div>
            <div className="text-white font-bold">{game.entryFee} USD</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">Grand Prize</div>
            <div className="text-hash-gold font-bold">{game.maxPrize.toLocaleString()} USD</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">Win Probability</div>
            <div className="text-medora-blue font-bold">
              {(game.totalWinProbability * 100).toFixed(game.tierNumber >= 4 ? 2 : 1)}%
            </div>
          </div>
          {game.neighbor2Count != null ? (
            <div>
              <div className="text-gray-500 text-xs mb-1">Neighbors</div>
              <div className="flex flex-wrap items-start gap-x-8 gap-y-2">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
                    Tier 1
                  </div>
                  <div className="text-white font-bold tabular-nums">
                    {game.neighborCount.toLocaleString()}
                  </div>
                  {game.neighborPrize != null ? (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {game.neighborPrize.toLocaleString()} USD each
                    </div>
                  ) : null}
                </div>
                <div className="hidden sm:block w-px self-stretch bg-white/10" aria-hidden />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
                    Tier 2
                  </div>
                  <div className="text-white font-bold tabular-nums">
                    {game.neighbor2Count.toLocaleString()}
                  </div>
                  {game.neighbor2Prize != null ? (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {game.neighbor2Prize.toLocaleString()} USD each
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-gray-500 text-xs mb-1">Neighbors</div>
              <div className="text-white font-bold tabular-nums">{game.neighborCount.toLocaleString()}</div>
              {game.neighborPrize != null ? (
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {game.neighborPrize.toLocaleString()} USD each
                </div>
              ) : null}
            </div>
          )}
          {roundError && !roundLoading && (
            <div className="col-span-2">
              <p className="text-xs text-amber-400/90" title={roundError}>
                On-chain data unavailable
              </p>
            </div>
          )}
          {game.instantPayout && (
            <div className="col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold">
                <img src="/images/icon-instant-win.svg" alt="" className="w-3 h-3" />
                Instant Real-Time Payout
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GameInfoPanel;
